import {channelsService, webanalyticsService} from 'services';
import fillChannelsWithConnectionMethod from 'store/modules/channels/utils/fillChannelsWithConnectionMethod';
import fillProvidersWithConnectionMethod from 'store/modules/channels/utils/fillProvidersWithConnectionMethod';

export const SET_CHANNELS = 'SET_CHANNELS';
export const FETCH_WA_PROVIDERS = 'FETCH_WA_PROVIDERS';
export const SET_WA_PROVIDERS = 'SET_WA_PROVIDERS';
export const FETCH_CHANNELS = 'FETCH_CHANNELS';

const OAUTH_SELECT_CHANNEL_NAMES_SET = new Set(['vkontakte', 'google', 'twitter', 'youtube']);

function channelGetter (state, channelNamesList, sourceKey = 'channels', compareKey = 'name') {
  const namesSet = new Set(channelNamesList);
  return state[sourceKey].filter(i => namesSet.has(i[compareKey]));
}

const channelsStore = {
  namespaced: true,
  state () {
    return {
      channels: [],
      waChannels: [],

      projectDetailedStatisticsChannelNames: [
        'yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'adfox', 'yandexmarket', 'youtube',
        'googledbm', 'googledcm', 'yandexdisplay', 'twitter', 'adriver', 'custom', 'ozon', 'tiktok', 'wildberries',
        'vkads'
      ],
      roreTrackingStatisticsChannelNames: ['googledcm', 'weborama'],
      balancingChannelNames: ['yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'vkads'],
      socialChannelNames: ['facebook'],
      bidmanagerChannelNames: [
        'yandex', 'google', 'facebook', 'vkontakte',
        'targetmail', 'ozon', 'wildberries', 'vkads'
      ],
      linkCheckingChannelNames: ['yandex', 'google'],
      crossminusChannelNames: ['yandex'],
      generationChannelNames: ['yandex', 'google'],
      utmTaggerChannelNames: ['yandex', 'google', 'facebook', 'vkontakte', 'targetmail', 'yandexmarket', 'vkads'],
      segmentsChannelNames: ['yandex', 'google', 'facebook', 'targetmail', 'vkontakte', 'vkads'],

      detailedStatisticsWaProviders: ['googleanalytics', 'yandexmetrika', 'appmetrica', 'amocrm', 'comagic', 'custom']
    };
  },

  getters: {
    channels: state => state.channels,
    enabledChannels: state => state.channels.filter(c => c.is_enabled),

    waChannels: state => state.waChannels,
    providers: state => state.waChannels.map(p => ({...p})),

    projectDetailedStatisticsChannels: state => channelGetter(state, state.projectDetailedStatisticsChannelNames),
    projectDetailedStatisticsChannelNames: state => state.projectDetailedStatisticsChannelNames,
    roreTrackingStatisticsChannels: state => channelGetter(state, state.roreTrackingStatisticsChannelNames),
    balancingChannels: state => channelGetter(state, state.balancingChannelNames),
    socialChannels: state => channelGetter(state, state.socialChannelNames),
    bidmanagerChannels: state => channelGetter(state, state.bidmanagerChannelNames),
    bidmanagerEnabledChannelsIds: (state, getters) => getters.bidmanagerChannels.
      filter(channel => channel.is_enabled).
      map(channel => channel.id),
    linkCheckingChannels: state => channelGetter(state, state.linkCheckingChannelNames),
    crossminusChannels: state => channelGetter(state, state.crossminusChannelNames),
    generationChannels: state => channelGetter(state, state.generationChannelNames),
    utmTaggerChannels: state => channelGetter(state, state.utmTaggerChannelNames),
    segmentsChannels: state => channelGetter(state, state.segmentsChannelNames),
    taggableChannels (state, getters, rootState, rootGetters) {
      const defaultTemplateChannelIds = new Set(rootGetters['common/appConfig'].DEFAULT_UTM_TEMPLATES_CHANNEL_IDS || []);
      return state.channels.filter(i => defaultTemplateChannelIds.has(i.id));
    },
    oauthSelectChannels: state => state.channels.filter(i => OAUTH_SELECT_CHANNEL_NAMES_SET.has(i.name)),

    getChannelById: state => (channelId) => state.channels.find(i => i.id === channelId),
    getChannelByName: state => (channelName) => state.channels.find(i => i.name === channelName),
    getEnabledChannelByName: state => (channelName) => state.channels.
      find(i => i.name === channelName && i.is_enabled),
    getChannelsIds: (state, getters) => (channelNames) => channelNames.map(i => getters.getChannelByName(i).id),

    getProviderById: (state, getters) => providerId => getters.providers.find(p => p.id === providerId),
    getProviderByName: (state, getters) => (providerName) => getters.providers.find(p => p.name === providerName),

    channelIcons: state => state.channels.reduce((acc, c) => {
      acc[c.id] = c.icon;
      return acc;
    }, {}),
    channelIds: state => state.channels.map(c => c.id),

    detailedStatisticsWaProviders: (state, getters) => state.detailedStatisticsWaProviders.map(
      p => getters.getProviderByName(p)
    )
  },

  mutations: {
    [SET_CHANNELS] (state, channelsList) {
      state.channels = fillChannelsWithConnectionMethod(channelsList).map(i => ({
        ...i,
        icon: channelsService.getIconByChannelName(i.name)
      }));
    },
    [SET_WA_PROVIDERS] (state, providers) {
      state.waChannels = fillProvidersWithConnectionMethod(providers).map(i => ({
        ...i,
        icon: webanalyticsService.getIconByChannelName(i.name)
      }));
    }
  },

  actions: {
    [SET_CHANNELS] (context, val) {
      context.commit(SET_CHANNELS, val);
    },
    async [FETCH_CHANNELS] (context) {
      const channels = await channelsService.getChannelsList();
      context.dispatch(SET_CHANNELS, channels);
    },
    async [FETCH_WA_PROVIDERS] (context) {
      const channelsList = await webanalyticsService.getChannels();
      context.commit(SET_WA_PROVIDERS, channelsList);
    }
  }
};

export default channelsStore;
